import { useEffect, useRef } from "react";

import TileLayer from "ol/layer/Tile";
import ImageLayer from "ol/layer/Image";
import TileWMS from "ol/source/TileWMS";
import ImageArcGISRest from "ol/source/ImageArcGISRest";
import WMTS from "ol/source/WMTS";

import { getTileserverStatuses } from "../map/mapUtil";
import { getOptionsFromCapabilities } from "../util";

import { useEnv } from "../contexts/envContext";
import { useUserAuth } from "../contexts/authContext";

const useWeatherLayer = (map, setUserMapLayers) => {
    const layerRefreshIntervalsRef = useRef([]);

    const { userMapSettings, userProfileSettings } = useUserAuth();
    const { weatherOptions, hasValidTruWeatherCredentials } = useEnv();

    useEffect(() => {
        if (!map || typeof hasValidTruWeatherCredentials !== "boolean" || Array.isArray(weatherOptions) === false || weatherOptions.length === 0) {
            return;
        }
        initializeWeatherLayers();

        return () => {
            layerRefreshIntervalsRef.current.forEach((interval) => {
                clearInterval(interval);
            });
        };
    }, [map, weatherOptions, hasValidTruWeatherCredentials]);

    const initializeWeatherLayers = async () => {
        // initially disable these layers
        setUserMapLayers((prev) => {
            const disabledWeatherLayers = prev.weather_radar.map((weatherLayer) => {
                return { ...weatherLayer, visible: false, disabled: true };
            });
            return { ...prev, weather_radar: disabledWeatherLayers };
        });

        // get tileserver statuses
        const statuses = await getTileserverStatuses();
        weatherOptions.forEach(async (option) => {
            // keep truweather options disabled if credentials are invalid
            if (option.type === "TRUWEATHER_WMTS" && hasValidTruWeatherCredentials !== true) {
                return;
            }
            const name = option.name.toLowerCase().replace(/ /g, "_");
            if (statuses && statuses[name] === true) {
                // update dropdown
                const existingLayer = userProfileSettings.layers.weather_radar.find((weatherLayer) => {
                    return weatherLayer.weather_name === option.name;
                });
                let setting = { weather_name: option.name, visible: true, disabled: false };
                if (existingLayer) {
                    setting = { weather_name: option.name, visible: existingLayer.visible, disabled: false };
                }
                setUserMapLayers((prev) => {
                    const updatedWeatherLayers = prev.weather_radar.map((prevLayer) => {
                        return prevLayer.weather_name === option.name ? setting : prevLayer;
                    });
                    return { ...prev, surveillance_sources_list: [], weather_radar: updatedWeatherLayers };
                });

                // add layer to the map
                let source = undefined;
                let layer = undefined;

                if (option.type === "WMS") {
                    source = new TileWMS(option.source);
                    layer = new TileLayer({ source: source, zIndex: 1 });
                } else if (option.type === "IMAGE_ARCGIS_REST") {
                    source = new ImageArcGISRest(option.source);
                    layer = new ImageLayer({ source: source, zIndex: 1 });
                } else if (option.type === "TRUWEATHER_WMTS") {
                    const options = await getOptionsFromCapabilities(option);
                    source = new WMTS(options);
                    layer = new TileLayer({ source: source, zIndex: 1 });
                }
                if (source) {
                    const interval = setInterval(() => {
                        if (typeof source.updateParams === "function") {
                            source.updateParams({ ts: Date.now() });
                        }
                        if (typeof source.clear === "function") {
                            source.clear();
                        }
                        if (typeof source.refresh === "function") {
                            source.refresh();
                        }
                    }, 1000 * 60);

                    layerRefreshIntervalsRef.current.push(interval);
                }
                if (layer) {
                    layer.setVisible(setting.visible);
                    layer.setOpacity(userMapSettings.weather_opacity);

                    layer.setProperties({ name: setting.weather_name });
                    map.addLayer(layer);
                }
            }
        });
    };
};

export default useWeatherLayer;
